import React, { useState } from 'react';
import { X, FileText, AlertCircle } from 'lucide-react';
import { MyFNGComplaintService } from '../../services/myfngComplaintService';

const MyFNGComplaintRegistration = ({ visible, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    chat_name: '',
    waba: '',
    car_model: '',
    cust_keyword: ''
  });

  const handleSubmit = async () => {
    // Validation
    if (!formData.chat_name.trim()) {
      alert('Please enter customer name');
      return;
    }
    if (!formData.waba.trim()) {
      alert('Please enter WhatsApp number');
      return;
    }
    if (!formData.car_model.trim()) {
      alert('Please enter car model');
      return;
    }
    if (!formData.cust_keyword.trim()) {
      alert('Please enter customer keyword/remark');
      return;
    }

    setLoading(true);
    try {
      const result = await MyFNGComplaintService.registerComplaint(formData, userId);
      
      if (result.success) {
        alert('MyFNG complaint registered successfully!');
        // Reset form
        setFormData({
          chat_name: '',
          waba: '',
          car_model: '',
          cust_keyword: ''
        });
        onClose();
      } else {
        alert(result.error || 'Failed to register complaint');
      }
    } catch (error) {
      console.error('MyFNG Registration Error:', error);
      let errorMessage = 'An error occurred while registering the complaint';
      
      if (error && error.message) {
        if (error.message.includes('JSON Parse')) {
          errorMessage = 'API response error. The complaint may have been registered. Please check.';
        } else {
          errorMessage = error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
      <div className="bg-white rounded-t-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Register MyFNG Complaint</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter customer name"
                value={formData.chat_name}
                onChange={(e) => setFormData({ ...formData, chat_name: e.target.value })}
              />
            </div>

            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                WhatsApp Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter WhatsApp Business API number"
                value={formData.waba}
                onChange={(e) => setFormData({ ...formData, waba: e.target.value })}
              />
            </div>

            {/* Car Model */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Car Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter car model"
                value={formData.car_model}
                onChange={(e) => setFormData({ ...formData, car_model: e.target.value })}
              />
            </div>

            {/* Customer Keyword/Remark */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Customer Keyword/Remark <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[100px]"
                placeholder="Enter customer keyword or remark"
                value={formData.cust_keyword}
                onChange={(e) => setFormData({ ...formData, cust_keyword: e.target.value })}
                rows={4}
              />
            </div>

            {/* Info Note */}
            <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <AlertCircle size={20} className="text-purple-600 mt-0.5" />
              <p className="text-sm text-gray-700">
                This complaint will be registered via MyFNG API and stored in the database.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-2 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Registering...</span>
              </>
            ) : (
              <>
                <FileText size={20} />
                <span>Register Complaint</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyFNGComplaintRegistration;

